import { useEffect, useState, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ResellerShell from './ResellerShell'
import ManagedInviteModal from '../../components/ManagedInviteModal'
import { tournamentApi } from '../../services/api'
import { useLanguage } from '../../context/LanguageContext'

/**
 * Groupings list — Phase 5 entry point.
 *
 * Shows all the reseller's non-archived groupings as cards. "+ New" opens
 * a quick-create modal that captures Name + Center + Radius and routes to
 * the full edit page.
 */
export default function GroupingsList() {
  const { t } = useLanguage()
  const navigate = useNavigate()
  const [groupings, setGroupings] = useState([])
  const [account, setAccount] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showNew, setShowNew] = useState(false)
  const [courseInviteGrouping, setCourseInviteGrouping] = useState(null)
  const [recentLink, setRecentLink] = useState(null)
  const [incoming, setIncoming] = useState([])
  const [menuOpenId, setMenuOpenId] = useState(null)
  const [duplicateOf, setDuplicateOf] = useState(null)
  const [shareOf, setShareOf] = useState(null)
  const [toast, setToast] = useState(null)
  // Labels + view controls (for resellers with many groupings)
  const [labels, setLabels] = useState([])
  const [activeLabelId, setActiveLabelId] = useState(null)   // null = All
  const [search, setSearch] = useState('')
  const [tileSize, setTileSize] = useState(0)                // 0 = large, 1 = medium, 2 = small
  const [labelPickerFor, setLabelPickerFor] = useState(null) // { ids:[...], title } for the Apply-Label panel
  const [showManageLabels, setShowManageLabels] = useState(false)

  const canInvite = account?.CanInvite !== 0 && account?.CanInvite !== false

  function loadIncoming() {
    tournamentApi.get('/portal/reseller/grouping-shares/incoming')
      .then(r => setIncoming(r.data?.shares || []))
      .catch(() => setIncoming([]))
  }

  function loadLabels() {
    tournamentApi.get('/portal/reseller/grouping-labels')
      .then(r => setLabels(r.data?.labels || []))
      .catch(() => setLabels([]))
  }

  function reload() {
    setLoading(true); setError(null)
    Promise.all([
      tournamentApi.get('/portal/reseller/groupings'),
      tournamentApi.get('/portal/reseller/me').catch(() => ({ data: null })),
    ])
      .then(([g, m]) => {
        setGroupings(g.data?.groupings || [])
        const acc = m.data?.account || null
        setAccount(acc)
        if (acc) setTileSize(Number(acc.GroupingsTileSize) || 0)
      })
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
    loadIncoming()
    loadLabels()
  }
  useEffect(reload, [])

  // Persist the tile-size choice to the account (survives navigation/devices).
  async function changeTileSize(size) {
    const prev = tileSize
    setTileSize(size)
    try { await tournamentApi.patch('/portal/reseller/prefs', { groupingsTileSize: size }) }
    catch (e) { setTileSize(prev); setError(e?.response?.data?.message || e.message) }
  }

  // Apply or remove a label on one or more groupings, then refresh.
  async function setLabelOn(labelId, groupingIds, add) {
    try {
      await tournamentApi.post(`/portal/reseller/grouping-labels/${labelId}/${add ? 'apply' : 'remove'}`, { groupingIds })
      reload()
    } catch (e) { setError(e?.response?.data?.message || e.message) }
  }

  async function respondShare(shareId, action, name) {
    try {
      await tournamentApi.post(`/portal/reseller/grouping-shares/${shareId}/${action}`)
      if (action === 'accept') {
        setToast(t('sp_share_accepted', 'Added "{name}" to your groupings as a draft.', { name: name || '' }))
        reload()
      } else {
        setToast(t('sp_share_rejected', 'Share declined.'))
        loadIncoming()
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message)
    }
  }

  // Hard delete — only when no money was ever involved anywhere in the
  // grouping. Pre-checks with the server so we can explain what's blocking,
  // then requires the user to type the grouping name (irreversible; also wipes
  // every package + golf bundle inside it).
  async function deleteGroupingPermanently(g) {
    setMenuOpenId(null)
    let info
    try {
      const { data } = await tournamentApi.get(`/portal/reseller/groupings/${g.ID_NO}/deletability`)
      info = data
    } catch (e) { setError(e?.response?.data?.message || e.message); return }
    if (!info?.canDelete) {
      const bits = []
      const bk = Object.entries(info?.breakdown || {}).map(([s, n]) => `${n} ${s}`).join(', ')
      if (bk) bits.push(bk)
      if (info?.bundlePurchases) bits.push(t('sp_delete_grp_bundle_purchases', '{n} bundle purchase(s)', { n: info.bundlePurchases }))
      alert(t('sp_delete_grp_blocked',
        'This grouping can’t be deleted because it has real activity ({list}). Archive it instead.',
        { list: bits.join(', ') || t('sp_bookings_word', 'bookings') }))
      return
    }
    const parts = []
    if (info.packageCount) parts.push(t('sp_n_packages', '{n} package(s)', { n: info.packageCount }))
    if (info.bundleCount)  parts.push(t('sp_n_bundles', '{n} golf bundle(s)', { n: info.bundleCount }))
    if (info.abandonedCarts) parts.push(t('sp_n_carts', '{n} abandoned cart(s)', { n: info.abandonedCarts }))
    const contains = parts.length ? t('sp_delete_grp_contains', ' It contains {list}.', { list: parts.join(', ') }) : ''
    const word = t('sp_confirm_word', 'CONFIRM')
    const typed = prompt(t('sp_delete_grp_prompt',
      'This permanently deletes “{name}” and everything in it. This cannot be undone.{contains}\n\nType {word} to confirm:',
      { name: g.Name, contains, word }))
    if (typed == null) return
    if (!['confirm', 'confirmer', 'yes', 'oui'].includes(typed.trim().toLowerCase())) {
      alert(t('sp_delete_not_confirmed', 'Not confirmed — nothing was deleted.'))
      return
    }
    try {
      await tournamentApi.delete(`/portal/reseller/groupings/${g.ID_NO}/permanent`)
      setToast(t('sp_grouping_deleted', 'Deleted “{name}”.', { name: g.Name }))
      reload()
    } catch (e) { setError(e?.response?.data?.message || e.message) }
  }

  // Client-side filter: active label chip + free-text search (name/address/description).
  const q = search.trim().toLowerCase()
  const filtered = groupings.filter(g => {
    if (activeLabelId && !(g.Labels || []).some(l => l.id === activeLabelId)) return false
    if (q) {
      const hay = `${g.Name || ''} ${g.CenterLabel || ''} ${g.Description || ''}`.toLowerCase()
      if (!hay.includes(q)) return false
    }
    return true
  })
  const filterActive = !!activeLabelId || !!q
  // Tile size drives column width + whether card details show.
  const tileMin = tileSize === 2 ? 190 : tileSize === 1 ? 260 : 360
  const showDetails = tileSize < 2

  return (
    <ResellerShell title={t('groupings', 'Groupings')}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 24 }}>
        <p style={{ color: '#64748b', maxWidth: 600, marginTop: 0, marginBottom: 0, fontSize: 14, lineHeight: 1.5 }}>
          {t('sp_grouping_intro', 'A grouping is a destination region (e.g. "Cape Breton") with a center + radius. Packages live inside groupings; each grouping gets its own customer-facing iframe. You can have as many groupings as you want.')}
          {canInvite && (
            <> {' '}<Link to="/portal/reseller/managed-accounts" style={{ color: '#2563eb' }}>{t('sp_managed_accounts', 'Managed accounts')}</Link> {t('sp_managed_accounts_intro', 'for reseller invites and all pending invitations.')}</>
          )}
        </p>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {canInvite && (
            <Link to="/portal/reseller/managed-accounts" style={secondaryLinkBtn}>{t('sp_managed_accounts', 'Managed accounts')}</Link>
          )}
          <button onClick={() => setShowNew(true)} style={primaryBtn}>{t('sp_new_grouping_btn', '+ New grouping')}</button>
        </div>
      </div>

      {recentLink && (
        <div style={successBox}>
          <div style={{ fontWeight: 600, color: '#065f46', marginBottom: 6 }}>
            {t('sp_course_invitation_sent', 'Course invitation sent')}
            {recentLink.emailSent === false && ' ' + t('sp_email_not_sent_copy_link', '(email not sent — copy the link)')}
          </div>
          <code style={linkCode}>{recentLink.url}</code>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => navigator.clipboard.writeText(recentLink.url)} style={secondaryBtn}>{t('sp_copy_link', 'Copy link')}</button>
            <button type="button" onClick={() => setRecentLink(null)} style={secondaryBtn}>{t('dismiss', 'Dismiss')}</button>
          </div>
        </div>
      )}

      {error && <div style={errorBox}>{error}</div>}
      {toast && <div style={successBox}><span style={{ color: '#065f46', fontWeight: 600 }}>{toast}</span></div>}

      {incoming.length > 0 && (
        <div style={sharedSection}>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#3730a3', marginBottom: 10 }}>
            {t('sp_shared_with_you', 'Shared with you')}
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            {incoming.map(s => (
              <div key={s.ID_NO} style={sharedRow}>
                <div style={{ fontSize: 14, color: '#1e293b' }}>
                  <strong>{s.FromResellerName}</strong>{' '}
                  {t('sp_is_sharing_grouping', 'is sharing a grouping with you:')}{' '}
                  <strong>{s.GroupingNameSnapshot}</strong>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button type="button" style={acceptBtn}
                    onClick={() => respondShare(s.ID_NO, 'accept', s.GroupingNameSnapshot)}>
                    {t('accept', 'Accept')}
                  </button>
                  <button type="button" style={secondaryBtn}
                    onClick={() => respondShare(s.ID_NO, 'reject')}>
                    {t('reject', 'Reject')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toolbar: search + compact toggle + manage labels (for many groupings) */}
      {!loading && groupings.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('sp_search_groupings', 'Search groupings…')}
            style={{ flex: '1 1 220px', minWidth: 180, padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 13 }}
          />
          <TileSizePicker value={tileSize} onChange={changeTileSize} />
          <button type="button" onClick={() => setShowManageLabels(true)} style={secondaryBtn}>{t('sp_manage_labels', 'Manage labels')}</button>
        </div>
      )}

      {/* Label filter chips */}
      {!loading && labels.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
          <FilterChip active={!activeLabelId} onClick={() => setActiveLabelId(null)} color="#334155" label={t('sp_all_label', 'All')} count={groupings.length} />
          {labels.map(l => (
            <FilterChip key={l.id} active={activeLabelId === l.id} onClick={() => setActiveLabelId(activeLabelId === l.id ? null : l.id)} color={l.color} label={l.name} count={l.groupingCount} />
          ))}
        </div>
      )}

      {/* Bulk apply when a filter/search narrows the list */}
      {!loading && filterActive && filtered.length > 0 && labels.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, fontSize: 13, color: '#475569' }}>
          <span>{t('sp_showing_n', 'Showing {n}', { n: filtered.length })}</span>
          <button type="button" style={secondaryBtn}
            onClick={() => setLabelPickerFor({ ids: filtered.map(g => g.ID_NO), title: t('sp_label_n_results', '{n} results', { n: filtered.length }), bulk: true })}>
            {t('sp_apply_label_to_results', 'Apply label to these {n} →', { n: filtered.length })}
          </button>
        </div>
      )}

      {loading
        ? <div style={empty}>{t('sp_loading_short', 'Loading…')}</div>
        : groupings.length === 0
          ? <div style={empty}>{t('sp_no_groupings_yet', 'No groupings yet. Click "+ New grouping" to create your first.')}</div>
          : filtered.length === 0
            ? <div style={empty}>{t('sp_no_matching_groupings', 'No groupings match your filter.')}</div>
            : (
              <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${tileMin}px, 1fr))`, gap: 10 }}>
                {filtered.map(g => (
                  <div key={g.ID_NO} style={card}>
                    <GroupingMenu
                      open={menuOpenId === g.ID_NO}
                      onToggle={() => setMenuOpenId(menuOpenId === g.ID_NO ? null : g.ID_NO)}
                      onApplyLabel={() => { setMenuOpenId(null); setLabelPickerFor({ ids: [g.ID_NO], title: g.Name, single: g }) }}
                      onDuplicate={() => { setMenuOpenId(null); setDuplicateOf(g) }}
                      onShare={() => { setMenuOpenId(null); setShareOf(g) }}
                      onDelete={() => deleteGroupingPermanently(g)}
                    />
                    <Link to={`/portal/reseller/groupings/${g.ID_NO}`} style={cardLinkInner}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, paddingRight: 28 }}>
                        <strong style={{ fontSize: tileSize === 2 ? 14 : 16, color: '#0f172a' }}>{g.Name}</strong>
                        <StatusBadge value={g.Status} />
                      </div>
                      {(g.Labels || []).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
                          {g.Labels.map(l => <LabelTag key={l.id} color={l.color} name={l.name} />)}
                        </div>
                      )}
                      {showDetails && (
                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>
                          {g.CenterLabel || `${g.CenterLatitude}, ${g.CenterLongitude}`} · {t('sp_radius_km_suffix', '{km}km radius', { km: g.RadiusKm })}
                        </div>
                      )}
                      {showDetails && g.Description && <div style={{ fontSize: 13, color: '#475569', marginBottom: 10, lineHeight: 1.4 }}>{g.Description}</div>}
                      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#64748b', marginTop: 10, paddingTop: 10, borderTop: '1px solid #f1f5f9' }}>
                        <span>{t('sp_course_rules', 'Course rules:')} <strong style={{ color: '#0f172a' }}>{g.IncludedCourses + g.ExcludedCourses + g.PreferredCourses}</strong></span>
                        <span>{t('sp_hotel_rules', 'Hotel rules:')} <strong style={{ color: '#0f172a' }}>{g.IncludedHotels + g.ExcludedHotels + g.PreferredHotels}</strong></span>
                      </div>
                    </Link>
                    {canInvite && (
                      <div style={cardActions}>
                        <button
                          type="button"
                          onClick={() => setCourseInviteGrouping(g)}
                          style={inviteTileBtn}
                        >
                          {t('sp_invite_course', 'Invite course')}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
      }

      {/* Account-wide search iframe — shows packages from every grouping
          in one widget. Each package can be hidden via its own
          IncludeInSearch toggle (default on). */}
      {account?.Slug && groupings.length > 0 && <AccountSearchEmbed slug={account.Slug} />}

      {showNew && <NewGroupingModal onClose={() => setShowNew(false)} onCreated={(id) => navigate(`/portal/reseller/groupings/${id}`)} />}

      {duplicateOf && (
        <DuplicateModal
          grouping={duplicateOf}
          onClose={() => setDuplicateOf(null)}
          onDone={(name) => { setDuplicateOf(null); setToast(t('sp_grouping_duplicated', 'Duplicated "{name}" as a draft.', { name })); reload() }}
        />
      )}
      {shareOf && (
        <ShareModal
          grouping={shareOf}
          onClose={() => setShareOf(null)}
          onDone={(name) => { setShareOf(null); setToast(t('sp_grouping_shared', 'Shared with {name}. They’ll see it under "Shared with you".', { name })) }}
        />
      )}

      {labelPickerFor && (
        <LabelPickerModal
          target={labelPickerFor}
          labels={labels}
          onClose={() => setLabelPickerFor(null)}
          onChanged={reload}
        />
      )}
      {showManageLabels && (
        <ManageLabelsModal
          labels={labels}
          onClose={() => setShowManageLabels(false)}
          onChanged={reload}
        />
      )}

      {courseInviteGrouping && (
        <ManagedInviteModal
          open
          onClose={() => setCourseInviteGrouping(null)}
          onSuccess={(data) => {
            setRecentLink({ url: data.acceptUrl, emailSent: data.emailSent })
            setCourseInviteGrouping(null)
          }}
          inviteType="course"
          groupingId={courseInviteGrouping.ID_NO}
          groupings={groupings}
          parentName={account?.Name || t('sp_your_partner', 'Your partner')}
        />
      )}
    </ResellerShell>
  )
}

function AccountSearchEmbed({ slug }) {
  const { t } = useLanguage()
  const origin = window.location.origin
  const url = `${origin}/embed/reseller-search/${slug}`
  // Auto-resizing iframe: the widget posts its content height and this listener
  // resizes the iframe to match — no fixed height, mobile-friendly. Harmless for
  // any host without the listener (they ignore the message).
  const snippet = `<iframe id="integolf-search" src="${url}" width="100%" scrolling="no" style="border:none;width:100%;min-height:600px;display:block;" loading="lazy"></iframe>
<script>
(function(){
  var f=document.getElementById('integolf-search');
  window.addEventListener('message',function(e){
    if(!f||!e.data||e.data.type!=='integolf-embed-resize')return;
    if(e.source&&f.contentWindow&&e.source!==f.contentWindow)return;
    var h=e.data.height;
    if(typeof h==='number'&&h>0){f.style.height=h+'px';}
  });
})();
</script>`
  const [copied, setCopied] = useState(false)
  function copy() {
    navigator.clipboard.writeText(snippet)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) })
      .catch(() => {})
  }

  // Default language the account-wide search widget opens in ('en' | 'fr').
  const [lang, setLang] = useState('en')
  const [savingLang, setSavingLang] = useState(false)
  useEffect(() => {
    tournamentApi.get('/portal/reseller/account/preferences')
      .then(r => setLang(r.data?.defaultLanguage === 'fr' ? 'fr' : 'en'))
      .catch(() => {})
  }, [])
  async function saveLang(next) {
    setLang(next); setSavingLang(true)
    try { await tournamentApi.patch('/portal/reseller/account/preferences', { defaultLanguage: next }) }
    catch { /* non-fatal — the toggle in the widget still works */ }
    finally { setSavingLang(false) }
  }
  return (
    <div style={{ marginTop: 32, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 20 }}>
      <h3 style={{ margin: '0 0 6px', fontSize: 15 }}>{t('sp_account_search_widget', 'Account-wide search widget')}</h3>
      <p style={{ margin: 0, color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
        {t('sp_account_search_desc', "One iframe customers can drop on their site to search across all your groupings + packages. Use the Include in search toggle on each package's edit page (default on) to opt individual packages out without unpublishing them.")}
      </p>
      <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>{t('sp_rsearch_setting_label', 'Default search language')}</label>
        <select value={lang} onChange={e => saveLang(e.target.value)} disabled={savingLang}
          style={{ padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}>
          <option value="en">{t('sp_lang_en', 'English')}</option>
          <option value="fr">{t('sp_lang_fr', 'French')}</option>
        </select>
        {savingLang && <span style={{ fontSize: 12, color: '#94a3b8' }}>…</span>}
      </div>
      <p style={{ margin: '6px 0 0', fontSize: 12, color: '#94a3b8', lineHeight: 1.4 }}>
        {t('sp_rsearch_setting_help', 'The language your account-wide search widget opens in. Visitors can still switch.')}
      </p>
      <div style={{ marginTop: 14, fontSize: 12, color: '#475569' }}>{t('sp_public_url', 'Public URL:')}</div>
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, fontFamily: 'monospace', fontSize: 12, marginTop: 4, wordBreak: 'break-all' }}>
        <a href={url} target="_blank" rel="noopener" style={{ color: '#0f172a', textDecoration: 'none' }}>{url}</a>
      </div>
      <div style={{ marginTop: 12, fontSize: 12, color: '#475569' }}>{t('sp_embed_code', 'Embed code:')}</div>
      <div style={{ position: 'relative', background: '#0f172a', color: '#e2e8f0', borderRadius: 6, padding: 12, fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}>
        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{snippet}</pre>
        <button
          type="button"
          onClick={copy}
          style={{
            position: 'absolute', top: 8, right: 8,
            padding: '4px 10px', background: copied ? '#16a34a' : '#475569',
            color: '#fff', border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer',
          }}
        >
          {copied ? t('sp_copied_2', 'Copied') : t('copy', 'Copy')}
        </button>
      </div>
    </div>
  )
}

function NewGroupingModal({ onClose, onCreated }) {
  const { t } = useLanguage()
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  async function submit(e) {
    e.preventDefault()
    setErr(null); setSubmitting(true)
    try {
      // Default center: middle of Canada. The edit page will let them pick a real spot.
      const { data } = await tournamentApi.post('/portal/reseller/groupings', {
        name,
        centerLatitude: 56.130366, centerLongitude: -106.346771,
        centerLabel: '(set on next screen)',
        radiusKm: 50,
      })
      onCreated(data.groupingId)
    } catch (e2) {
      setErr(e2?.response?.data?.message || e2.message)
    } finally { setSubmitting(false) }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalCard} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18 }}>{t('sp_new_grouping_heading', 'New grouping')}</h3>
        {err && <div style={errorBox}>{err}</div>}
        <form onSubmit={submit}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('sp_name_label', 'Name')}</label>
          <input
            autoFocus required type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder={t('sp_grouping_name_placeholder', 'Cape Breton, Halifax Coast, etc.')}
            style={inp}
          />
          <p style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>{t('sp_set_center_next_screen', "You'll set the center location and radius on the next screen.")}</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>
            <button type="submit" disabled={submitting || !name.trim()} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>
              {submitting ? t('creating', 'Creating…') : t('sp_create_edit', 'Create + edit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function GroupingMenu({ open, onToggle, onApplyLabel, onDuplicate, onShare, onDelete }) {
  const { t } = useLanguage()
  return (
    <>
      {open && <div style={{ position: 'fixed', inset: 0, zIndex: 5 }} onClick={onToggle} />}
      <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 6 }}>
        <button type="button" onClick={onToggle} aria-label="Menu" style={dotsBtn}>⋮</button>
        {open && (
          <div style={menuDropdown}>
            <button type="button" style={menuItem} onClick={onApplyLabel}>{t('sp_apply_label', 'Apply label')}</button>
            <button type="button" style={menuItem} onClick={onDuplicate}>{t('sp_duplicate', 'Duplicate')}</button>
            <button type="button" style={menuItem} onClick={onShare}>{t('sp_share', 'Share')}</button>
            <button type="button" style={{ ...menuItem, color: '#b91c1c' }} onClick={onDelete}>{t('sp_delete', 'Delete')}</button>
          </div>
        )}
      </div>
    </>
  )
}

function DuplicateModal({ grouping, onClose, onDone }) {
  const { t } = useLanguage()
  const [name, setName] = useState(`${grouping.Name} (copy)`)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)
  async function submit(e) {
    e.preventDefault(); setErr(null); setSubmitting(true)
    try {
      await tournamentApi.post(`/portal/reseller/groupings/${grouping.ID_NO}/duplicate`, { name: name.trim() })
      onDone(name.trim())
    } catch (e2) { setErr(e2?.response?.data?.message || e2.message) }
    finally { setSubmitting(false) }
  }
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalCard} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>{t('sp_duplicate_grouping', 'Duplicate grouping')}</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
          {t('sp_duplicate_desc', 'Creates a draft copy of everything in this grouping — courses, hotels, packages, branding and settings.')}
        </p>
        {err && <div style={errorBox}>{err}</div>}
        <form onSubmit={submit}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('sp_new_name', 'New name')}</label>
          <input autoFocus type="text" value={name} onChange={e => setName(e.target.value)} style={inp} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>
            <button type="submit" disabled={submitting || !name.trim()} style={{ ...primaryBtn, opacity: (submitting || !name.trim()) ? 0.6 : 1 }}>
              {submitting ? t('sp_duplicating', 'Duplicating…') : t('sp_duplicate', 'Duplicate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function ShareModal({ grouping, onClose, onDone }) {
  const { t } = useLanguage()
  const [email, setEmail] = useState('')
  const [check, setCheck] = useState({ state: 'idle' }) // idle | checking | found | notfound
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)
  const timer = useRef(null)

  function onEmailChange(v) {
    setEmail(v); setErr(null)
    if (timer.current) clearTimeout(timer.current)
    const val = v.trim()
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(val)) { setCheck({ state: 'idle' }); return }
    setCheck({ state: 'checking' })
    timer.current = setTimeout(async () => {
      try {
        const { data } = await tournamentApi.get('/portal/reseller/grouping-shares/reseller-lookup', { params: { email: val } })
        setCheck(data?.found ? { state: 'found', name: data.name } : { state: 'notfound' })
      } catch { setCheck({ state: 'notfound' }) }
    }, 400)
  }

  async function submit(e) {
    e.preventDefault(); if (check.state !== 'found') return
    setErr(null); setSubmitting(true)
    try {
      const { data } = await tournamentApi.post(`/portal/reseller/groupings/${grouping.ID_NO}/share`, { email: email.trim() })
      onDone(data?.sharedWith || check.name)
    } catch (e2) { setErr(e2?.response?.data?.message || e2.message) }
    finally { setSubmitting(false) }
  }

  const borderColor = check.state === 'found' ? '#16a34a' : check.state === 'notfound' ? '#dc2626' : '#e2e8f0'
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalCard} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>{t('sp_share_grouping', 'Share grouping')}</h3>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
          {t('sp_share_desc', 'Send a copy of "{name}" to another reseller. They accept it under "Shared with you" and get their own draft copy.', { name: grouping.Name })}
        </p>
        {err && <div style={errorBox}>{err}</div>}
        <form onSubmit={submit}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t('sp_reseller_email', "Reseller's email")}</label>
          <input autoFocus type="email" value={email} onChange={e => onEmailChange(e.target.value)}
            placeholder="partner@example.com" style={{ ...inp, borderColor }} />
          <div style={{ minHeight: 20, marginTop: 6, fontSize: 13 }}>
            {check.state === 'checking' && <span style={{ color: '#64748b' }}>{t('sp_checking', 'Checking…')}</span>}
            {check.state === 'found' && <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ {t('sp_sharing_with', 'Sharing with {name}', { name: check.name })}</span>}
            {check.state === 'notfound' && <span style={{ color: '#dc2626', fontWeight: 600 }}>{t('sp_reseller_not_found', 'Reseller not found')}</span>}
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
            <button type="button" onClick={onClose} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>
            <button type="submit" disabled={submitting || check.state !== 'found'} style={{ ...primaryBtn, opacity: (submitting || check.state !== 'found') ? 0.5 : 1 }}>
              {submitting ? t('sp_sharing', 'Sharing…') : t('sp_share', 'Share')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function StatusBadge({ value }) {
  const { t } = useLanguage()
  const colors = {
    draft:    { bg: '#fef3c7', fg: '#92400e' },
    active:   { bg: '#dcfce7', fg: '#166534' },
    paused:   { bg: '#e2e8f0', fg: '#475569' },
    archived: { bg: '#f1f5f9', fg: '#94a3b8' },
  }
  const c = colors[value] || colors.draft
  return <span style={{ padding: '2px 8px', background: c.bg, color: c.fg, borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{t('sp_status_' + value, value)}</span>
}

// ─── Labels ─────────────────────────────────────────────────────────────

const LABEL_PALETTE = ['#64748b', '#ef4444', '#f59e0b', '#16a34a', '#0ea5e9', '#8b5cf6', '#ec4899', '#14b8a6']

function ColorDots({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {LABEL_PALETTE.map(c => (
        <button key={c} type="button" onClick={() => onChange(c)} aria-label={c}
          style={{ width: 20, height: 20, borderRadius: '50%', background: c, cursor: 'pointer', border: value === c ? '2px solid #0f172a' : '2px solid transparent' }} />
      ))}
    </div>
  )
}

function FilterChip({ active, onClick, color, label, count }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999,
      border: `1px solid ${active ? color : '#e2e8f0'}`, background: active ? color : '#fff',
      color: active ? '#fff' : '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer',
    }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: active ? '#fff' : color }} />
      {label}
      {count != null && <span style={{ opacity: 0.7 }}>{count}</span>}
    </button>
  )
}

function LabelTag({ color, name }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 999, fontSize: 11, fontWeight: 600, color, background: color + '18', border: `1px solid ${color}44` }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
      {name}
    </span>
  )
}

// Apply-label panel from the ⋮ menu (single grouping) or the bulk "apply to N"
// action. Single mode shows checkboxes of the grouping's current labels; bulk
// mode applies a chosen label to every grouping in the set.
function LabelPickerModal({ target, labels, onClose, onChanged }) {
  const { t } = useLanguage()
  const single = target.single
  const [appliedIds, setAppliedIds] = useState(new Set((single?.Labels || []).map(l => l.id)))
  const [creating, setCreating] = useState(labels.length === 0)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState(LABEL_PALETTE[0])
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  async function toggle(labelId, add) {
    setBusy(true); setErr(null)
    // optimistic for the single-grouping checkboxes
    if (single) setAppliedIds(prev => { const n = new Set(prev); add ? n.add(labelId) : n.delete(labelId); return n })
    try {
      await tournamentApi.post(`/portal/reseller/grouping-labels/${labelId}/${add ? 'apply' : 'remove'}`, { groupingIds: target.ids })
      onChanged()
    } catch (e) {
      setErr(e?.response?.data?.message || e.message)
      if (single) setAppliedIds(prev => { const n = new Set(prev); add ? n.delete(labelId) : n.add(labelId); return n })
    } finally { setBusy(false) }
  }

  async function createAndApply(e) {
    e.preventDefault()
    const name = newName.trim(); if (!name) return
    setBusy(true); setErr(null)
    try {
      const { data } = await tournamentApi.post('/portal/reseller/grouping-labels', { name, color: newColor })
      await tournamentApi.post(`/portal/reseller/grouping-labels/${data.id}/apply`, { groupingIds: target.ids })
      if (single) setAppliedIds(prev => new Set(prev).add(data.id))
      setNewName(''); setCreating(false); onChanged()
    } catch (e2) { setErr(e2?.response?.data?.message || e2.message) }
    finally { setBusy(false) }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modalCard, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 4px', fontSize: 17 }}>{t('sp_apply_label', 'Apply label')}</h3>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 14 }}>{target.title}</div>
        {err && <div style={errorBox}>{err}</div>}

        {creating ? (
          <form onSubmit={createAndApply} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} placeholder={t('sp_label_name_ph', 'Label name')} maxLength={60} style={{ ...inp, flex: '1 1 140px', width: 'auto' }} />
            <ColorDots value={newColor} onChange={setNewColor} />
            <button type="submit" disabled={busy || !newName.trim()} style={primaryBtn}>{t('sp_create_apply', 'Create + apply')}</button>
            {labels.length > 0 && <button type="button" onClick={() => setCreating(false)} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>}
          </form>
        ) : (
          <button type="button" onClick={() => setCreating(true)} style={{ ...secondaryBtn, marginBottom: 12 }}>{t('sp_create_label', '+ Create label')}</button>
        )}

        <div style={{ display: 'grid', gap: 4, maxHeight: 280, overflowY: 'auto' }}>
          {labels.map(l => {
            const has = appliedIds.has(l.id)
            return (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, background: '#f8fafc' }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: l.color, flexShrink: 0 }} />
                <span style={{ flex: 1, fontSize: 13, color: '#0f172a' }}>{l.name}</span>
                {single ? (
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#475569', cursor: 'pointer' }}>
                    <input type="checkbox" checked={has} disabled={busy} onChange={e => toggle(l.id, e.target.checked)} />
                    {has ? t('sp_applied', 'Applied') : t('sp_apply', 'Apply')}
                  </label>
                ) : (
                  <button type="button" disabled={busy} onClick={() => toggle(l.id, true)} style={secondaryBtn}>{t('sp_apply_to_all', 'Apply to all')}</button>
                )}
              </div>
            )
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose} style={secondaryBtn}>{t('done', 'Done')}</button>
        </div>
      </div>
    </div>
  )
}

// Create / rename / recolor / delete labels. Deleting only unassigns.
function ManageLabelsModal({ labels, onClose, onChanged }) {
  const { t } = useLanguage()
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)
  const [newName, setNewName] = useState('')
  const [newColor, setNewColor] = useState(LABEL_PALETTE[0])
  const [editId, setEditId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editColor, setEditColor] = useState(LABEL_PALETTE[0])

  async function create(e) {
    e.preventDefault(); const name = newName.trim(); if (!name) return
    setBusy(true); setErr(null)
    try { await tournamentApi.post('/portal/reseller/grouping-labels', { name, color: newColor }); setNewName(''); onChanged() }
    catch (e2) { setErr(e2?.response?.data?.message || e2.message) } finally { setBusy(false) }
  }
  function startEdit(l) { setEditId(l.id); setEditName(l.name); setEditColor(l.color) }
  async function saveEdit() {
    const name = editName.trim(); if (!name) return
    setBusy(true); setErr(null)
    try { await tournamentApi.put(`/portal/reseller/grouping-labels/${editId}`, { name, color: editColor }); setEditId(null); onChanged() }
    catch (e2) { setErr(e2?.response?.data?.message || e2.message) } finally { setBusy(false) }
  }
  async function del(l) {
    if (!confirm(t('sp_delete_label_confirm', 'Delete label “{name}”? It is removed from all groupings — the groupings themselves are not affected.', { name: l.name }))) return
    setBusy(true); setErr(null)
    try { await tournamentApi.delete(`/portal/reseller/grouping-labels/${l.id}`); onChanged() }
    catch (e2) { setErr(e2?.response?.data?.message || e2.message) } finally { setBusy(false) }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modalCard, maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 14px', fontSize: 17 }}>{t('sp_manage_labels', 'Manage labels')}</h3>
        {err && <div style={errorBox}>{err}</div>}
        <form onSubmit={create} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder={t('sp_new_label_ph', 'New label…')} maxLength={60} style={{ ...inp, flex: '1 1 140px', width: 'auto' }} />
          <ColorDots value={newColor} onChange={setNewColor} />
          <button type="submit" disabled={busy || !newName.trim()} style={primaryBtn}>{t('sp_add_label', 'Add')}</button>
        </form>
        <div style={{ display: 'grid', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
          {labels.length === 0 && <div style={{ fontSize: 13, color: '#94a3b8' }}>{t('sp_no_labels_yet', 'No labels yet — create one above.')}</div>}
          {labels.map(l => (
            <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, borderRadius: 6, background: '#f8fafc', flexWrap: 'wrap' }}>
              {editId === l.id ? (
                <>
                  <input value={editName} onChange={e => setEditName(e.target.value)} maxLength={60} style={{ ...inp, flex: '1 1 160px', width: 'auto' }} />
                  <ColorDots value={editColor} onChange={setEditColor} />
                  <button type="button" disabled={busy || !editName.trim()} onClick={saveEdit} style={primaryBtn}>{t('save', 'Save')}</button>
                  <button type="button" onClick={() => setEditId(null)} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>
                </>
              ) : (
                <>
                  <span style={{ width: 12, height: 12, borderRadius: 3, background: l.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13, color: '#0f172a' }}>{l.name}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{t('sp_label_groupings_count', '{n} groupings', { n: l.groupingCount })}</span>
                  <button type="button" onClick={() => startEdit(l)} style={secondaryBtn}>{t('edit', 'Edit')}</button>
                  <button type="button" onClick={() => del(l)} style={{ ...secondaryBtn, color: '#b91c1c' }}>{t('sp_delete', 'Delete')}</button>
                </>
              )}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button type="button" onClick={onClose} style={secondaryBtn}>{t('done', 'Done')}</button>
        </div>
      </div>
    </div>
  )
}

// Segmented control: pick how big the grouping tiles are (density icons).
function GridIcon({ n }) {
  const size = 14, gap = 2
  const cell = (size - gap * (n - 1)) / n
  const cells = []
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
    cells.push(<rect key={`${r}-${c}`} x={c * (cell + gap)} y={r * (cell + gap)} width={cell} height={cell} rx={1} fill="currentColor" />)
  }
  return <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>{cells}</svg>
}

function TileSizePicker({ value, onChange }) {
  const { t } = useLanguage()
  const opts = [
    { v: 0, label: t('sp_size_large', 'Large tiles'), n: 1 },
    { v: 1, label: t('sp_size_medium', 'Medium tiles'), n: 2 },
    { v: 2, label: t('sp_size_small', 'Small tiles'), n: 3 },
  ]
  return (
    <div style={{ display: 'inline-flex', border: '1px solid #cbd5e1', borderRadius: 8, overflow: 'hidden', flexShrink: 0 }} title={t('sp_tile_size', 'Tile size')}>
      {opts.map((o, i) => (
        <button key={o.v} type="button" onClick={() => onChange(o.v)} title={o.label} aria-label={o.label} aria-pressed={value === o.v}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 32,
            background: value === o.v ? '#0f172a' : '#fff', color: value === o.v ? '#fff' : '#64748b',
            border: 'none', borderLeft: i ? '1px solid #e2e8f0' : 'none', cursor: 'pointer',
          }}>
          <GridIcon n={o.n} />
        </button>
      ))}
    </div>
  )
}

const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'visible', position: 'relative' }
const cardLinkInner = { display: 'block', padding: 18, textDecoration: 'none', color: 'inherit' }
const cardActions = { padding: '0 18px 14px', borderTop: '1px solid #f1f5f9', paddingTop: 12 }
const inviteTileBtn = { width: '100%', padding: '8px 12px', background: '#f8fafc', color: '#0f172a', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const secondaryLinkBtn = { display: 'inline-flex', alignItems: 'center', padding: '10px 16px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontWeight: 600, textDecoration: 'none' }
const successBox = { padding: 16, marginBottom: 20, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8 }
const linkCode = { display: 'block', background: '#fff', padding: '8px 10px', borderRadius: 6, fontSize: 12, wordBreak: 'break-all', marginTop: 4 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const primaryBtn = { padding: '10px 20px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '10px 16px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, cursor: 'pointer' }
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }
const modalCard = { background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 480 }
const dotsBtn = { width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: 0 }
const menuDropdown = { position: 'absolute', top: 32, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 6px 24px rgba(15,23,42,0.12)', minWidth: 150, overflow: 'hidden', zIndex: 7 }
const menuItem = { display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', background: '#fff', border: 'none', borderBottom: '1px solid #f1f5f9', fontSize: 14, color: '#0f172a', cursor: 'pointer' }
const sharedSection = { background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, padding: 16, marginBottom: 20 }
const sharedRow = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, background: '#fff', border: '1px solid #e0e7ff', borderRadius: 6, padding: '10px 14px' }
const acceptBtn = { padding: '8px 16px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
